/**
 * Item builder pricing engine.
 *
 * One quote line = products + decorations + additional costs, all at the
 * same quantity. Setup costs are spread across the quantity and each row
 * carries its own markup.
 *
 *   unit cost = buy + extra per unit + setup / qty
 *   unit sell = unit cost × (1 + markup)        (rounded to the cent)
 *   row sell  = unit sell × qty
 *
 * Rounding rule (matches Hoops, verified on quote 1632): the unit sell is
 * worked out from the UNROUNDED unit cost, then rounded to the cent, and the
 * row total is the rounded unit sell × qty. Costs are not rounded until
 * display.
 *
 * This file is pure (no database, no network) so it can run in the browser
 * editor and in the edge function that saves quotes, giving the same answer
 * in both places.
 */

export type RowKind = 'product' | 'decoration' | 'additional';
export type PriceLogic = 'cost_plus_markup' | 'set_gross_margin';

export interface PriceBreak {
  qty: number; // break applies from this qty upward
  price: number;
}

export interface MarkupRange {
  min_price: number; // unit buy price range, inclusive
  max_price: number | null; // null = no upper limit
  min_qty: number;
  percent: number; // e.g. 99 for 99%
}

export interface BuildRow {
  kind: RowKind;
  description: string;
  /** Flat buy price. Ignored when price_breaks is set. */
  buy_price?: number;
  /** Buy price by quantity; the break with the highest qty ≤ line qty wins. */
  price_breaks?: PriceBreak[];
  /** Extra cost per unit (e.g. an extra print position). */
  extra_per_unit?: number;
  /** One-off setup cost, spread over the line quantity. */
  setup?: number;
  /** Markup % for this row. If omitted, taken from the markup table. */
  markup_percent?: number;
  /** Gross margin % for this row, used when price logic = set_gross_margin. */
  margin_percent?: number;
  /** Manual unit sell price (the pencil icon). Markup is back-calculated. */
  sell_override?: number;
  /** Decorations: show as their own line on the customer quote. */
  display_on_quote?: boolean;
  /** Catalogue references, kept for the record (not used in pricing). */
  decoration_id?: string;
  product_id?: string;
  vendor?: string;
}

export interface Build {
  qty: number;
  price_logic?: PriceLogic;
  rows: BuildRow[];
}

export interface PricingContext {
  markups?: MarkupRange[];
  /** Customer minimum markup %, to raise a warning. */
  customer_min_markup?: number;
}

export interface RowResult {
  description: string;
  kind: RowKind;
  buy_unit: number;
  unit_cost: number; // unrounded
  markup_percent: number; // effective markup after any override
  unit_sell: number; // rounded to cents
  total_cost: number; // unrounded
  total_sell: number; // cents-exact
  display_on_quote: boolean;
}

export interface LineResult {
  qty: number;
  rows: RowResult[];
  total_cost: number; // rounded to cents
  total_sell: number;
  unit_sell: number; // total sell ÷ qty, rounded to cents
  gross_margin_percent: number; // rounded to 2 dp
  warnings: string[];
}

/** Round to cents, half away from zero, safe against float noise. */
export function round2(n: number): number {
  const sign = n < 0 ? -1 : 1;
  return (sign * Math.round(Math.abs(n) * 100 + 1e-9)) / 100;
}

/** Buy price for a qty from price breaks (highest break ≤ qty). */
export function priceForQty(breaks: PriceBreak[], qty: number): number {
  const sorted = [...breaks].sort((a, b) => a.qty - b.qty);
  if (sorted.length === 0) throw new Error('No price breaks');
  let price = sorted[0].price; // below the first break, use the first break
  for (const b of sorted) if (qty >= b.qty) price = b.price;
  return price;
}

/** First markup range matching unit buy price and qty, else null. */
export function lookupMarkup(
  ranges: MarkupRange[] | undefined,
  unitBuy: number,
  qty: number,
): number | null {
  for (const r of ranges ?? []) {
    const aboveMin = unitBuy >= r.min_price;
    const belowMax = r.max_price == null || unitBuy <= r.max_price;
    if (aboveMin && belowMax && qty >= r.min_qty) return r.percent;
  }
  return null;
}

/** markup = margin ÷ (1 − margin), in percent. */
export function marginToMarkup(marginPercent: number): number {
  const m = marginPercent / 100;
  if (m >= 1) throw new Error('Gross margin must be below 100%');
  return (m / (1 - m)) * 100;
}

export function priceLine(build: Build, ctx: PricingContext = {}): LineResult {
  const { qty } = build;
  if (!(qty > 0)) throw new Error('Quantity must be greater than zero');
  const logic = build.price_logic ?? 'cost_plus_markup';
  const warnings: string[] = [];

  const rows: RowResult[] = build.rows.map((row) => {
    const buyUnit = row.price_breaks?.length
      ? priceForQty(row.price_breaks, qty)
      : row.buy_price ?? 0;
    const unitCost = buyUnit + (row.extra_per_unit ?? 0) + (row.setup ?? 0) / qty;

    let markup: number;
    let unitSell: number;

    if (row.sell_override != null) {
      unitSell = round2(row.sell_override);
      markup = unitCost > 0 ? (unitSell / unitCost - 1) * 100 : 0;
    } else {
      if (logic === 'set_gross_margin' && row.margin_percent != null) {
        markup = marginToMarkup(row.margin_percent);
      } else if (row.markup_percent != null) {
        markup = row.markup_percent;
      } else {
        const fromTable = lookupMarkup(ctx.markups, buyUnit, qty);
        if (fromTable == null) {
          warnings.push(`No markup set or found for "${row.description}"; using 0%`);
        }
        markup = fromTable ?? 0;
      }
      unitSell = round2(unitCost * (1 + markup / 100));
    }

    if (ctx.customer_min_markup != null && markup < ctx.customer_min_markup - 1e-9) {
      warnings.push(
        `"${row.description}" markup ${markup.toFixed(1)}% is below the customer minimum of ${ctx.customer_min_markup}%`,
      );
    }

    return {
      description: row.description,
      kind: row.kind,
      buy_unit: buyUnit,
      unit_cost: unitCost,
      markup_percent: markup,
      unit_sell: unitSell,
      total_cost: unitCost * qty,
      total_sell: round2(unitSell * qty),
      display_on_quote: row.kind === 'product' ? true : row.display_on_quote ?? false,
    };
  });

  const totalCost = round2(rows.reduce((s, r) => s + r.total_cost, 0));
  const totalSell = round2(rows.reduce((s, r) => s + r.total_sell, 0));
  const margin = totalSell > 0 ? ((totalSell - totalCost) / totalSell) * 100 : 0;

  return {
    qty,
    rows,
    total_cost: totalCost,
    total_sell: totalSell,
    unit_sell: round2(totalSell / qty),
    gross_margin_percent: round2(margin),
    warnings,
  };
}

/** Quote totals: GST applied per line, then summed. */
export function quoteTotals(lines: { total_sell: number; tax_rate: number }[]) {
  const subtotal = round2(lines.reduce((s, l) => s + l.total_sell, 0));
  const tax = round2(lines.reduce((s, l) => s + round2(l.total_sell * l.tax_rate), 0));
  return { subtotal, tax, total: round2(subtotal + tax) };
}
